import {Component, ElementRef, HostListener, Input, OnDestroy, ViewChild} from '@angular/core';

@Component({
  selector: 'core-audio-widget',
  imports: [],
  templateUrl: './audio-widget.html',
  styleUrl: './audio-widget.scss',
  standalone: true,
  host: {
    style: 'padding: 1rem;display: flex;flex-direction: row;width: 100%;border-radius:2rem;gap: 0.75rem;align-items: center;',
    class: 'bg-neutral-200'
  }
})
export class AudioWidget implements OnDestroy {
  @Input() src: string | null = null;
  @Input() disabled = false;
  @ViewChild('audioEl') audioEl?: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;
  isMuted = false;
  volume = 0.8;
  showMenu = false;
  showVolume = false;

  ngOnDestroy() {
    this.stop();
  }

  togglePlay() {
    if (this.disabled || !this.src || !this.audioEl) return;
    const audio = this.audioEl.nativeElement;
    if (this.isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    const host = this.audioEl?.nativeElement?.closest('core-audio-widget');
    if (!host) return;
    if (target && host.contains(target)) return;
    this.showMenu = false;
    this.showVolume = false;
  }

  handleLoadedMetadata() {
    const audio = this.audioEl?.nativeElement;
    if (!audio) return;
    this.duration = audio.duration || 0;
    audio.volume = this.volume;
    audio.muted = this.isMuted;
  }

  handleTimeUpdate() {
    const audio = this.audioEl?.nativeElement;
    if (!audio) return;
    this.currentTime = audio.currentTime || 0;
  }

  handlePlay() {
    this.isPlaying = true;
  }

  handlePause() {
    this.isPlaying = false;
  }

  handleEnded() {
    this.isPlaying = false;
    this.currentTime = 0;
  }

  seek(event: MouseEvent) {
    if (this.disabled || !this.audioEl || !this.duration) return;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const audio = this.audioEl.nativeElement;
    audio.currentTime = ratio * this.duration;
  }

  toggleMute() {
    if (this.disabled || !this.audioEl) return;
    this.isMuted = !this.isMuted;
    this.audioEl.nativeElement.muted = this.isMuted;
  }

  changeVolume(event: Event) {
    if (this.disabled || !this.audioEl) return;
    const input = event.target as HTMLInputElement;
    const next = Number(input.value);
    if (Number.isNaN(next)) return;
    this.volume = Math.min(1, Math.max(0, next));
    this.audioEl.nativeElement.volume = this.volume;
    if (this.volume === 0) {
      this.isMuted = true;
      this.audioEl.nativeElement.muted = true;
    } else if (this.isMuted) {
      this.isMuted = false;
      this.audioEl.nativeElement.muted = false;
    }
  }

  get progressPercent(): number {
    if (!this.duration) return 0;
    return Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100));
  }

  get timeLabel(): string {
    return `${this.formatTime(this.currentTime)} / ${this.formatTime(this.duration)}`;
  }

  private formatTime(seconds: number): string {
    if (!seconds || Number.isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private stop() {
    if (!this.audioEl) return;
    const audio = this.audioEl.nativeElement;
    audio.pause();
    audio.currentTime = 0;
  }
}
